import { computed, ref, reactive, toValue, unref, toRef } from 'vue'
import { yupResolver } from '@primevue/forms/resolvers/yup'
import * as yup from 'yup'

export function useFormBuilder(formRef, formValidations) {

  formValidations = toRef(formValidations) // toRef allows us to pass either plain objects or reactive objects as values
  const resolver  = computed(() => yupResolver( yup.object().shape(formValidations.value) ))

  // ----------------------------
  // Raw helper methods - extract info from PrimeVue Form elem ref
  // ----------------------------
  const rawFields = computed(() => formRef.value?.fields)
  const rawStates = computed(() => formRef.value?.states)
  const rawErrors = computed(() => {
    // rawErrors extracts yup ValidationError objects for each field
    // See: https://github.com/jquense/yup?tab=readme-ov-file#validationerrorerrors-string--arraystring-value-any-path-string
    let array = Object.entries(rawStates.value || [])
    array = array.map(([key, fieldState]) => [key, fieldState.errors])
    array = array.filter(([key, yupErrors]) => yupErrors?.length > 0)
    return Object.fromEntries(array)
  })

  // ----------------------------
  // Simple helper methods
  // ----------------------------
  const errors = computed(() => {
    // errors simplifies rawErrors by returning only an array of error messages for each field
    const keys = Object.keys(rawErrors.value || {})
    let result = {}
    keys.forEach((key) => {
      const arr = rawErrors.value[key]
      result[key] = arr.map((yupError) => yupError.errors).flat()
    })
    return result
  })

  // simple hash of form field values using name as the key
  const values = computed(() => {
    const raw = rawStates.value
    const entries = Array.isArray(raw) ? raw.map((val, idx) => [String(idx), val]) : Object.entries(raw || {})
    const array = entries.map(([key, fieldState]) => [key, extractFieldValue(fieldState)])
    return Object.fromEntries(array)
  })

  // const isValid     = computed(() => numErrors.value == 0)
  const isValid     = computed(() => formRef.value?.valid)
  const isInvalid   = computed(() => !isValid.value)
  const isChanged   = computed(() => numChanges.value > 0)
  const numErrors   = computed(() => Object.keys(errors.value || {}).length || 0)
  const numChanges  = computed(() => dirtyFields.value?.length || 0)
  const numTouched  = computed(() => touchedFields.value?.length || 0)
  const numFields   = computed(() => Object.keys(rawFields.value || {}).length || 0)

  const touchedFields = computed(() => {
    return Object.entries(rawStates.value || {}).filter(([key, val]) => val.touched)
                 .map(([key, val]) => key)
  })
  const dirtyFields = computed(() => {
    return Object.entries(rawStates.value || {}).filter(([key, val]) => val.dirty)
                 .map(([key, val]) => key)
  })
  const pristineFields = computed(() => {
    return Object.entries(rawStates.value || {}).filter(([key, val]) => val.pristine)
                 .map(([key, val]) => key)
  })

  // Make setValues func available as appForm.setValues(...)
  const setValues = (vals) => formRef.value.setValues(vals)

  // Make reset func available as appForm.reset() - clears values back to
  // initialValues and resets touched/dirty/invalid state so validation
  // errors don't show until the next submit
  const reset = () => formRef.value.reset()

  return {
    isValid,
    isInvalid,
    isChanged,
    numErrors,
    numChanges,
    numTouched,
    numFields,
    errors,
    values,
    touchedFields,
    pristineFields,
    dirtyFields,
    setValues,
    reset,
    // here we use unref() to avoid having to add `form.resolver.value` in our templates:
    //    <Form :resolver="appForm.resolver"> instead of <Form :resolver="appForm.resolver.value">
    resolver: unref(resolver),
    // // Other helpers (not needed to export but doing so for testing purposes)
    // rawFields,
    // rawErrors,
    // rawStates,
  }
}

function extractFieldValue(fieldState) {
  if (Array.isArray(fieldState)) {
    return fieldState.map((item) => {
      const entries = Object.entries(item)
      const array = entries.map(([key, innerFieldState]) => [key, extractFieldValue(innerFieldState)])
      return Object.fromEntries(array)
    })
  }
  if (fieldState && typeof fieldState === 'object' && 'value' in fieldState) {
    return fieldState.value
  }
  return fieldState
}

// -------------------------------
// We export yup here to avoid needing to import it separately in other files
// Example import in components:
//    import { yup, useFormBuilder } from '@/use/useFormBuilder'
// -------------------------------
export * as yup from 'yup'
